import React from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import { AnswerByField } from '@/components/hiring/OfferChoiceDialogs';
import { nomineeAnswerByRange } from '@/shared/utils/deadline';

const LATEST_DAYS = 60;

const normalizeEmail = (value) =>
    typeof value === 'string' ? value.trim().toLowerCase() : '';

/**
 * The students a faculty member named for LA, one per address. Mirrors the
 * server's nomineeRowsOf: with 'selecting' or 'needs_help' the form hides the
 * rows, so they are not nominees.
 */
export function nomineesOf(application) {
    const la = application?.programSpecificResponses?.la;
    const status = la?.nomineeStatus;
    if (status !== undefined && status !== null && status !== 'selected') {
        return [];
    }
    const seen = new Set();
    const nominees = [];
    for (const row of application?.namedStudents || []) {
        if (row.forProgram !== 'la') continue;
        const email = normalizeEmail(row.email);
        if (email === '' || seen.has(email)) continue;
        seen.add(email);
        nominees.push({ email, name: (row.name || '').trim() || email });
    }
    return nominees;
}

/**
 * How many LAs the application requests: a whole number, 1 or more. Mirrors
 * the server's requestedCountFor, which is also how many an award can offer.
 */
export function requestedLACount(application) {
    const count = application?.programSpecificResponses?.la?.requestedLACount;
    return Number.isInteger(count) && count >= 1 ? count : 1;
}

/**
 * The choice the picker starts from: every nominee, or the first few when
 * there are more nominees than places.
 */
export function defaultOffered(nominees, limit) {
    return nominees.slice(0, limit).map((nominee) => nominee.email);
}

/**
 * Checkboxes for the nominees an LA award offers, capped at the number of
 * places. The parent owns the choice and sends it as `offeredEmails`, and
 * the date the nominees have to answer as `nomineeRespondByDay`, counted from
 * `releaseDate` (the program's decisions release) when that is still ahead. The date
 * shows only when `showAnswerBy` is set (the hiring flag is on): with hiring
 * off the nominees are told nothing yet, and the server gives them their
 * deadline when it is switched on.
 */
function NomineeOfferPicker({
    nominees,
    limit,
    offered,
    onChange,
    showAnswerBy = false,
    answerBy = '',
    onAnswerByChange,
    releaseDate = null,
}) {
    if (nominees.length === 0) return null;

    const answerRange = nomineeAnswerByRange(releaseDate, LATEST_DAYS);
    const atLimit = offered.length >= limit;
    const toggle = (email, checked) => {
        onChange(
            checked
                ? [...offered, email]
                : offered.filter((chosen) => chosen !== email)
        );
    };
    const places = `${limit} ${limit === 1 ? 'place' : 'places'}`;

    return (
        <fieldset className="mb-3" aria-describedby="adp-nominee-offer-help">
            <legend className="h6">Nominees to offer</legend>
            <Form.Text id="adp-nominee-offer-help" as="p" className="mb-2">
                The faculty member named these students. Choose up to {limit} to
                offer when you approve. The others are not offered.
            </Form.Text>
            {nominees.map((nominee, index) => {
                const checked = offered.includes(nominee.email);
                return (
                    <Form.Check
                        key={nominee.email}
                        type="checkbox"
                        id={`adp-nominee-offer-${index}`}
                        className="mb-1 py-2"
                        label={
                            <>
                                {nominee.name}{' '}
                                {nominee.name !== nominee.email && (
                                    <span className="text-muted small ms-2">
                                        {nominee.email}
                                    </span>
                                )}
                            </>
                        }
                        checked={checked}
                        disabled={atLimit && !checked}
                        onChange={(e) =>
                            toggle(nominee.email, e.target.checked)
                        }
                    />
                );
            })}
            {showAnswerBy && offered.length > 0 && (
                <div className="mt-3">
                    <AnswerByField
                        id="adp-nominee-answer-by"
                        value={answerBy}
                        onChange={onAnswerByChange}
                        min={answerRange.min}
                        max={answerRange.max}
                        emptyMeans="Leave it empty to use the program's usual number of days. Nominees answer by this date."
                    />
                </div>
            )}
            <p className="small mb-0" role="status">
                {offered.length} of {limit} chosen.
                {atLimit && nominees.length > offered.length
                    ? ` The other nominees are unavailable because ${places} ${
                          limit === 1 ? 'is' : 'are'
                      } taken. Untick one to choose someone else.`
                    : ''}
                {offered.length === 0 &&
                    ' No one will be offered a place from this list.'}
            </p>
        </fieldset>
    );
}

NomineeOfferPicker.propTypes = {
    nominees: PropTypes.arrayOf(
        PropTypes.shape({
            email: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        })
    ).isRequired,
    limit: PropTypes.number.isRequired,
    offered: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
    showAnswerBy: PropTypes.bool,
    answerBy: PropTypes.string,
    onAnswerByChange: PropTypes.func,
};

export default NomineeOfferPicker;
